import { clamp, prefersReducedMotion, waitForMedia } from '@theme/utilities';

/** Lead distance for fetching a `fixed` backdrop before it is scrolled into view. */
const PRELOAD_MARGIN = '600px 0px';

/**
 * MediaParallaxElement - scroll-linked parallax for media (image, video).
 */
class MediaParallaxElement extends HTMLElement {
  #mediaElements = [];
  #scrollHandler = null;
  #rafId = null;
  #preloadObserver = null;

  get parallaxDepth() {
    return this.hasAttribute('data-parallax-depth')
      ? parseFloat(this.getAttribute('data-parallax-depth'))
      : 0.2;
  }

  get parallaxEffect() {
    return this.getAttribute('data-parallax-effect') || 'vertical';
  }

  get media() {
    return this.#mediaElements;
  }

  connectedCallback() {
    this.#mediaElements = Array.from(
      this.querySelectorAll('img, deferred-media, iframe, svg')
    ).filter((el) => el.matches('deferred-media') || !el.closest('deferred-media'));
    if (this.#mediaElements.length === 0) return;

    // Runs regardless of whether a scroll-linked transform follows: the host keeps its children
    // at opacity 0 until `.loaded` lands, so skipping this hides the media entirely.
    this.#markLoadedWhenReady();

    const parallaxEffect = this.parallaxEffect;

    // A `fixed` backdrop is revealed the moment its section edge crosses the viewport, with no
    // slack for the browser's own lazy heuristic — fetch it before the scroll gets there.
    if (parallaxEffect === 'fixed') this.#preloadWhenNear();

    // `fixed` is positioned purely in CSS and never reaches #applyParallax, so there is nothing
    // to hint, listen for, or measure.
    if (prefersReducedMotion() || !this.parallaxDepth || parallaxEffect === 'fixed') return;

    const parallaxDepth = this.parallaxDepth;
    const [scale, maxTranslatePercent] = [1 + parallaxDepth, (parallaxDepth * 100) / (1 + parallaxDepth)];

    // Set transform origin & will-change once.
    const origin = parallaxEffect === 'vertical'
      ? 'bottom'
      : parallaxEffect === 'horizontal'
        ? 'right'
        : 'center';
    this.#mediaElements.forEach((el) => {
      el.style.transformOrigin = origin;
      el.style.willChange = 'transform';
    });

    this.#scrollHandler = () => this.#onScroll(scale, maxTranslatePercent, parallaxEffect);
    window.addEventListener('scroll', this.#scrollHandler, { passive: true });

    this.#onScroll(scale, maxTranslatePercent, parallaxEffect);
  }

  /** Starts fetching still-lazy media once the host comes within PRELOAD_MARGIN of the viewport. */
  #preloadWhenNear() {
    const lazy = this.#mediaElements.filter(
      (el) =>
        el instanceof HTMLImageElement &&
        el.loading === 'lazy' &&
        !(el.complete && el.naturalWidth > 0)
    );
    if (!lazy.length) return;

    this.#preloadObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        lazy.forEach((el) => {
          el.loading = 'eager';
        });
        this.#preloadObserver?.disconnect();
        this.#preloadObserver = null;
      },
      { rootMargin: PRELOAD_MARGIN }
    );
    this.#preloadObserver.observe(this);
  }

  /** Reveals the media once it has finished loading. Fires once per element lifetime. */
  #markLoadedWhenReady() {
    setTimeout(() => {
      void waitForMedia(this.#mediaElements).then(() => this.classList.add('loaded'));
    }, 200);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#scrollHandler);
    this.#preloadObserver?.disconnect();
    this.#preloadObserver = null;
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#mediaElements.forEach((el) => {
      el.style.transform = '';
      el.style.transformOrigin = '';
      el.style.willChange = '';
    });
    this.#mediaElements = [];
  }

  #onScroll(scale, maxTranslatePercent, parallaxEffect) {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight;
      if (rect.height <= 0) return;
      const rangeStart = vh;
      const rangeEnd = -rect.height;
      const progress = clamp((rangeStart - rect.top) / (rangeStart - rangeEnd), 0, 1);
      this.#applyParallax(scale, maxTranslatePercent, parallaxEffect, progress);
    });
  }

  #applyParallax(scale, maxTranslatePercent, parallaxEffect, scrollProgress) {
    const translatePercent = maxTranslatePercent * scrollProgress;
    const zoomScale = 1 + (scale - 1) * scrollProgress;

    this.#mediaElements.forEach((el) => {
      if (parallaxEffect === 'vertical') {
        el.style.transform = `scale(${scale}) translateY(${translatePercent}%)`;
      } else if (parallaxEffect === 'horizontal') {
        el.style.transform = `scale(${scale}) translateX(${translatePercent}%)`;
      } else if (parallaxEffect === 'zoom') {
        el.style.transform = `scale(${zoomScale})`;
      }
    });
  }
}

if (!customElements.get('media-parallax')) {
  customElements.define('media-parallax', MediaParallaxElement);
}

/**
 * ImageElement is a custom element that extends the native HTMLImageElement.
 *
 * Its primary purpose is to optimize image loading and display by:
 * - Lazily loading images using IntersectionObserver, so images are only loaded when they are about to enter the viewport.
 * - Applying a "loaded" CSS class to both the image and its wrapper element when the image has finished loading, enabling smooth transitions or effects.
 * - Handling image load and error events, dispatching custom events ("imageloaded" and "imageerror") for further integration.
 * - Providing graceful fallback for browsers that do not support IntersectionObserver.
 * - Marking images and their wrappers with an "error" class if loading fails, and logging errors to the console.
 *
 * This class is registered as a custom element with the name "image-component" and is intended to be used with the "is" attribute on <img> tags.
 */

class ImageElement extends HTMLImageElement {
  #observer = null;
  #wrapper = null;
  #hasIntersected = false;
  #ioSupported = "IntersectionObserver" in window;

  /** Observes the image, or resumes the load path directly when a move reconnects an observed image. */
  connectedCallback() {
    this.#wrapper = this.closest('.image-component');

    if (this.#hasIntersected) {
      this.#loadImage();
      return;
    }

    if (this.#ioSupported) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.#hasIntersected) {
            this.#hasIntersected = true;
            this.#observer.disconnect();

            if (this.#isImageLoaded()) {
              this.#setLoaded();
            } else {
              this.#loadImage();
            }
          }
        },
        { root: null, rootMargin: '0px 0px -10%', threshold: 0.01 }
      );

      this.#observer.observe(this);
      return;
    }

    // Fallback: no IntersectionObserver
    this.#loadImage();
  }

  /** Drops the observer only: the load listeners still have to deliver `.loaded` after a move. */
  disconnectedCallback() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  /**
   * Called when the element is updated by morph
   * Adds loaded class to skip animation after morph
   */
  updatedCallback() {
    const wrapper = this.closest('.image-component');
    if (wrapper && !wrapper.classList.contains('loaded')) {
      wrapper.classList.add('loaded');
    }
  }

  /**
   * Checks if image is already loaded
   * @returns {boolean} True if image is loaded
   */
  #isImageLoaded() {
    return this.complete && this.naturalWidth > 0;
  }

  #loadImage() {
    this.addEventListener('load', this.#onLoad, { once: true });
    this.addEventListener('error', this.#onError, { once: true });

    // Check again in case image loaded between connectedCallback and here
    if (this.#isImageLoaded()) {
      this.#setLoaded();
    }
  }

  #onLoad = () => {
    this.#setLoaded();
    this.dispatchEvent(new CustomEvent('imageloaded', { bubbles: true }));
  };

  #onError = () => {
    this.#wrapper?.classList.add('error');
    this.classList.add('error');
    console.error('Image failed to load:', this.src);
    this.dispatchEvent(new CustomEvent('imageerror', { bubbles: true }));
  };

  async #setLoaded() {
    if (this.classList.contains('loaded') || this.#wrapper?.classList.contains('loaded')) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('loaded');
        this.#wrapper?.classList.add('loaded');
      });
    });

  }
}

if (!customElements.get('image-component')) {
  customElements.define('image-component', ImageElement, { extends: 'img' });
}